import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChefHat, Check, Clock, Undo2 } from "lucide-react";

import { Button, Card, EmptyState, Pill, SkeletonRows } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { ordersRepository, type Order, type OrderStatus } from "@/repositories/ordersRepository";

/**
 * The screen on the wall in the kitchen.
 *
 * Designed for a person with floury hands, standing up, glancing at it from a
 * metre away. That drives everything about it:
 *
 * *   **Big tickets, one column of them.** Not a table. Somebody scanning for
 *     "what do I make next" reads the top of a list, not a grid of cells.
 * *   **One tap per state.** Every extra control is one more thing to do between
 *     turning a burger, and a screen that takes four taps per order gets ignored
 *     in favour of shouting — which is what it replaced.
 * *   **The extras are the loudest text on the ticket.** "No atchar" is the
 *     whole content of the instruction; the product name is context.
 * *   **Unpaid is flagged in red.** Somebody handing over a bag needs to know
 *     before they let go of it.
 *
 * It polls rather than holding a local copy. A cached queue would keep showing
 * an order a colleague had already handed over, and two people would make the
 * same kota — the exact failure this screen exists to prevent.
 */
export function KitchenPage() {
  const queryClient = useQueryClient();

  const orders = useQuery({
    queryKey: ["orders", "kitchen"],
    queryFn: () => ordersRepository.kitchen(),
    // Ten seconds. Fast enough that a cook is not making something already
    // cancelled; slow enough not to hammer a shared connection all service.
    refetchInterval: 10_000,
  });

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      ordersRepository.setStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["orders"] }),
  });

  const rows = orders.data ?? [];
  const waiting = rows.filter((order) => order.status !== "READY");
  const ready = rows.filter((order) => order.status === "READY");

  if (orders.isLoading) {
    return (
      <Card>
        <SkeletonRows rows={4} />
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={ChefHat}
        title="Nothing waiting"
        description="Orders taken at the till appear here the moment they are rung up."
      />
    );
  }

  return (
    <div className="space-y-5">
      <Section title="To make" count={waiting.length}>
        {waiting.map((order) => (
          <Ticket
            key={order.id}
            order={order}
            busy={move.isPending && move.variables?.id === order.id}
            onMove={(status) => move.mutate({ id: order.id, status })}
          />
        ))}
      </Section>

      {ready.length > 0 && (
        <Section title="Ready to hand over" count={ready.length}>
          {ready.map((order) => (
            <Ticket
              key={order.id}
              order={order}
              busy={move.isPending && move.variables?.id === order.id}
              onMove={(status) => move.mutate({ id: order.id, status })}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium uppercase tracking-wide text-ink-500">
        {title} · {count}
      </h2>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{children}</div>
    </section>
  );
}

function Ticket({
  order,
  busy,
  onMove,
}: {
  order: Order;
  busy: boolean;
  onMove: (status: OrderStatus) => void;
}) {
  const waited = minutesSince(order.created_at);

  return (
    <Card
      className={cn(
        "flex flex-col gap-3 p-4",
        // Twenty minutes is where a customer standing at a counter starts
        // asking. The border is the only thing on this screen that changes
        // colour with time, so it means one thing.
        waited >= 20 && order.status !== "READY" && "border-warn-500",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span>
          <span className="text-2xl font-bold tabular-nums text-ink-900">#{order.number}</span>
          {order.customer_name && (
            <span className="ml-2 text-sm text-ink-600">{order.customer_name}</span>
          )}
        </span>

        <span className="flex flex-col items-end gap-1">
          {!order.is_paid && (
            <Pill tone="danger">
              <AlertTriangle size={12} aria-hidden /> Not paid
            </Pill>
          )}
          <span className="flex items-center gap-1 text-xs tabular-nums text-ink-500">
            <Clock size={12} aria-hidden />
            {waited} min
          </span>
        </span>
      </div>

      <ul className="space-y-2">
        {order.lines.map((line) => (
          <li key={line.id}>
            <span className="text-base font-medium text-ink-900">
              {Number(line.quantity) > 1 && `${Number(line.quantity)} × `}
              {line.description}
            </span>

            {/* The instruction, not the garnish. A cook reads this line and
                nothing else on a busy service. */}
            {(line.modifiers.length > 0 || line.note) && (
              <span className="mt-0.5 block text-sm font-semibold text-warn-700">
                {[...line.modifiers.map((modifier) => modifier.name), line.note]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </li>
        ))}
      </ul>

      {order.note && <p className="text-sm text-ink-600">{order.note}</p>}

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        <span className="text-xs tabular-nums text-ink-500">{formatMoney(order.total)}</span>

        <span className="flex gap-2">
          {order.status === "NEW" && (
            <Button size="sm" loading={busy} onClick={() => onMove("PREPARING")}>
              Start
            </Button>
          )}
          {order.status === "PREPARING" && (
            <Button variant="primary" size="sm" loading={busy} onClick={() => onMove("READY")}>
              <Check size={14} aria-hidden /> Ready
            </Button>
          )}
          {order.status === "READY" && (
            <>
              {/* Tapped by mistake with the side of a hand more often than
                  anybody would like. Putting it back beats cancelling a paid
                  order and ringing it up again. */}
              <Button variant="ghost" size="sm" loading={busy} onClick={() => onMove("PREPARING")}>
                <Undo2 size={14} aria-hidden />
                <span className="sr-only">Back to being made</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={busy}
                onClick={() => onMove("HANDED_OVER")}
              >
                Handed over
              </Button>
            </>
          )}
        </span>
      </div>
    </Card>
  );
}

/** Whole minutes since an order was taken. What a cook actually judges urgency by. */
function minutesSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
}
